import { Box, Icon, Text } from "@chakra-ui/react"
import { Link } from "@chakra-ui/react/link"
import { HStack, VStack } from "@chakra-ui/react/stack"
import { Stack } from "@chakra-ui/react/stack"
import parse from "html-react-parser"

import Logo from "@/components/Logo/Logo"
import NextLink from "@/components/RouteTransitionLink"
import siteConfig from "@/config/siteConfig"
import { getSafeHtmlParserOptions } from "@/utils/getSafeHtmlParserOptions"

const COPYRIGHT_YEAR = new Date().getFullYear()

const Footer = () => {
  return (
    <VStack
      alignItems="flex-start"
      as="footer"
      borderTop={"1px solid {colors.border}"}
      justifyContent="flex-start"
      px={0}
      py={0}
      rounded="0"
      w="full"
    >
      <VStack
        bgColor="bg.paper"
        gap={0}
        maxW="8xl"
        mx="auto"
        overflow={["visible", "visible", "visible", "visible"]}
        position={"relative"}
        rounded="0"
        textAlign={"center"}
        w="full"
        width="100%"
        zIndex={1}
      >
        <Box w="full">
          <Stack
            borderLeft={"1px solid {colors.border}"}
            borderRight={"1px solid {colors.border}"}
            flexDirection={{ base: "column", md: "row" }}
            gap={{ base: 6, md: 0 }}
            justifyContent="space-between"
            px={{ base: 6, md: 2 }}
            py={{ base: 6, md: 2 }}
            width="100%"
          >
            <HStack
              css={{
                "& svg": {
                  height: "1rem",
                  width: "1rem",
                },
              }}
              flexBasis={"1"}
              flexGrow={1}
              flexShrink={0}
              gap={{ base: 2, md: 1 }}
              justifyContent={{ base: "space-between", md: "flex-start" }}
            >
              {siteConfig.menuItems.map((item) => {
                if (item.active) {
                  return (
                    <Link
                      _dark={{
                        _hover: { bg: "gray.900" },
                        color: "gray.200",
                      }}
                      _hover={{
                        _dark: {
                          color: "primary.300",
                        },
                        bg: "gray.100",
                        color: "primary.700",
                        paddingX: 4,
                        textDecoration: "none",
                      }}
                      alignItems="center"
                      asChild
                      bg={"transparent"}
                      color="fg.muted"
                      cursor={"pointer"}
                      data-umami-event={"footer-link-" + item.slug}
                      display="flex"
                      fontFamily={"body"}
                      fontSize={"sm"}
                      gap={2}
                      height={10}
                      key={item.id}
                      px={3}
                      py={0}
                      rounded="md"
                      transition="background-color 0.2s ease-in-out, padding 0.2s ease-in-out, color 0.2s ease-in-out"
                      w={{ base: "auto" }}
                      whiteSpace="nowrap"
                    >
                      <NextLink
                        href={item.slug}
                        transitionTypes={[
                          item.slug === "/" ? "nav-back" : "nav-forward",
                        ]}
                      >
                        {undefined !== item.svg_icon
                          ? parse(item.svg_icon, getSafeHtmlParserOptions())
                          : ""}
                        <span>{item.name}</span>
                      </NextLink>
                    </Link>
                  )
                } else return ""
              })}
            </HStack>

            <HStack
              css={{
                "& svg": {
                  height: ".9rem",
                  width: ".9rem",
                },
              }}
              flexBasis={"1"}
              flexGrow={1}
              flexShrink={0}
              gap={{ base: 6, md: 4 }}
              justifyContent={{ base: "center", md: "flex-end" }}
            >
              {Object.values(siteConfig.socialLinks ?? {}).map((item: any) => (
                <Link
                  _dark={{
                    _hover: { bg: "gray.900", color: "primary.300" },
                    color: "gray.200",
                  }}
                  _hover={{
                    bg: "gray.100",
                    color:
                      "social." + (item.name || item.slug || "").toLowerCase(),
                    paddingX: 4,
                  }}
                  alignItems="center"
                  aria-label={item.name}
                  bg="transparent"
                  color="gray.600"
                  data-umami-event={
                    "social-link-" +
                    (item.slug || item.name || "").toLowerCase()
                  }
                  display="flex"
                  height={10}
                  href={item.url || "#"}
                  key={(item.slug || item.name || "").toLowerCase()}
                  px={3}
                  py={0}
                  rounded="md"
                  target="_blank"
                  transition="background-color 0.2s ease-in-out, padding 0.2s ease-in-out, color 0.2s ease-in-out"
                  w={{ base: "auto" }}
                >
                  {item.svg_icon ? (
                    <Icon size={"xs"}>
                      {parse(item.svg_icon, getSafeHtmlParserOptions())}
                    </Icon>
                  ) : (
                    ""
                  )}
                </Link>
              ))}
            </HStack>
          </Stack>
        </Box>
        <Box w="full">
          <Stack
            borderLeft={"1px solid {colors.border}"}
            borderRight={"1px solid {colors.border}"}
            borderTop={"1px solid {colors.border}"}
            className="bottom-footer"
            flexDirection={{ base: "column", md: "row" }}
            gap={4}
            justifyContent="space-between"
            justifyItems="stretch"
            px={{ base: 6 }}
            py={{ base: 4 }}
            rounded="0"
            width="100%"
          >
            <HStack order={{ base: 2, md: 1 }} pb={{ base: 2, md: 0 }}>
              <Logo size={18} />

              <Text
                color="fg.muted"
                fontFamily={"body"}
                fontSize={{ base: "xs" }}
                marginLeft={2}
              >
                {`${COPYRIGHT_YEAR} Gil Alvaro © All rights reserved`}
              </Text>
            </HStack>

            <HStack
              gap={4}
              justifyContent={{ base: "space-between", md: "flex-end" }}
              order={{ base: 1, md: 2 }}
              wrap="wrap"
            >
              <Link
                _hover={{
                  backgroundSize: "100% 1px",
                  color: "fg",
                  textDecoration: "none",
                }}
                alignItems="center"
                asChild
                backgroundImage="linear-gradient(currentColor, currentColor)"
                backgroundPosition="0 100%"
                backgroundRepeat="no-repeat"
                backgroundSize={"0% 1px"}
                bg={"transparent"}
                color="fg.muted"
                data-umami-event={"privacy-policy"}
                display="flex"
                fontFamily={"body"}
                fontSize={{ base: "xs" }}
                rounded="0"
                textDecoration="none"
                textWrap="nowrap"
                transition="color 180ms ease, background-size 300ms cubic-bezier(.2,.7,.2,1)"
                w={{ base: "auto" }}
                whiteSpace="nowrap"
              >
                <NextLink
                  href="/privacy-policy"
                  transitionTypes={["nav-forward"]}
                >
                  Privacy Policy
                </NextLink>
              </Link>
              <Link
                _hover={{
                  backgroundSize: "100% 1px",
                  color: "fg",
                  textDecoration: "none",
                }}
                alignItems="center"
                asChild
                backgroundImage="linear-gradient(currentColor, currentColor)"
                backgroundPosition="0 100%"
                backgroundRepeat="no-repeat"
                backgroundSize={"0% 1px"}
                bg={"transparent"}
                color="fg.muted"
                data-umami-event={"terms-of-service"}
                display="flex"
                fontFamily={"body"}
                fontSize={{ base: "xs" }}
                rounded="md"
                textDecoration="none"
                textWrap="nowrap"
                transition="color 180ms ease, background-size 300ms cubic-bezier(.2,.7,.2,1)"
                w={{ base: "auto" }}
                whiteSpace="nowrap"
              >
                <NextLink
                  href="/terms-of-service"
                  transitionTypes={["nav-forward"]}
                >
                  Terms of Service
                </NextLink>
              </Link>
              <Link
                _hover={{
                  backgroundSize: "100% 1px",
                  color: "fg",
                  textDecoration: "none",
                }}
                alignItems="center"
                asChild
                backgroundImage="linear-gradient(currentColor, currentColor)"
                backgroundPosition="0 100%"
                backgroundRepeat="no-repeat"
                backgroundSize={"0% 1px"}
                bg={"transparent"}
                color="fg.muted"
                display="flex"
                fontFamily={"body"}
                fontSize={{ base: "xs" }}
                rounded="md"
                textDecoration="none"
                textWrap="nowrap"
                transition="color 180ms ease, background-size 300ms cubic-bezier(.2,.7,.2,1)"
                w={{ base: "auto" }}
                whiteSpace="nowrap"
              >
                <NextLink href="/cookies" transitionTypes={["nav-forward"]}>
                  Cookies
                </NextLink>
              </Link>
              <Link
                _hover={{
                  backgroundSize: "100% 1px",
                  color: "fg",
                  textDecoration: "none",
                }}
                alignItems="center"
                backgroundPosition="0 100%"
                backgroundRepeat="no-repeat"
                backgroundSize={"0% 1px"}
                bg={"transparent"}
                color="fg.muted"
                data-umami-event={"sitemap"}
                display="flex"
                fontFamily={"body"}
                fontSize={{ base: "xs" }}
                href="/sitemap.xml"
                rounded="md"
                textDecoration="none"
                textWrap="nowrap"
                transition="color 180ms ease, background-size 300ms cubic-bezier(.2,.7,.2,1)"
                w={{ base: "auto" }}
                whiteSpace="nowrap"
              >
                Sitemap
              </Link>
            </HStack>
          </Stack>
        </Box>
      </VStack>
    </VStack>
  )
}

export default Footer
